import { useId, useState, type FormEvent } from 'react';
import type { AreaIncidencia, Incidencia, NivelIncidencia } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';

const NIVELES: NivelIncidencia[] = [1, 2, 3, 4];
const AREAS: AreaIncidencia[] = [
  'INTERIOR',
  'EXTERIOR',
  'SISTEMAS',
  'PISCINA',
  'JARDIN',
  'GESTION',
  'OTRO',
];

/** El importe se escribe en euros y viaja en centimos, como todo el dinero. */
function aCentimos(texto: string): number | undefined {
  const limpio = texto.replace(/\s/g, '').replace(',', '.');
  if (limpio === '') return undefined;
  const n = Number(limpio);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100);
}

interface Props {
  idioma: Idioma;
  propiedadId: string;
  /** Se avisa con la incidencia ya creada, por si quien llama quiere abrirla. */
  onCreada: (incidencia: Incidencia) => void;
  onCancelar: () => void;
}

/**
 * Dar de alta una incidencia.
 *
 * Vive en su propio componente porque se abre desde dos sitios: la pestana de
 * incidencias de una casa y la propia incidencia, cuando al mirar una aparece
 * otra. Duplicar el formulario habria hecho que uno de los dos se quedara sin
 * el campo que se anadiera despues.
 */
export function NuevaIncidencia({ idioma, propiedadId, onCreada, onCancelar }: Props) {
  const ids = {
    titulo: useId(),
    nivel: useId(),
    descripcion: useId(),
    area: useId(),
    coste: useId(),
  };
  const [titulo, setTitulo] = useState('');
  const [nivel, setNivel] = useState<NivelIncidencia>(2);
  const [descripcion, setDescripcion] = useState('');
  const [area, setArea] = useState<AreaIncidencia>('OTRO');
  const [coste, setCoste] = useState('');
  const [fotos, setFotos] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = (evento: FormEvent) => {
    evento.preventDefault();
    setEnviando(true);
    setError(null);
    const costeCents = aCentimos(coste);
    void (async () => {
      try {
        const { incidencia } = await api.crearIncidencia(propiedadId, {
          titulo,
          nivel,
          area,
          ...(descripcion.trim() ? { descripcion: descripcion.trim() } : {}),
          ...(costeCents === undefined ? {} : { costeEstimadoCents: costeCents }),
        });
        /*
         * Las fotos se suben DESPUES, una a una, y con la incidencia ya creada:
         * sin ella no hay a que colgarlas. Si una falla, las anteriores ya estan
         * guardadas y la incidencia tambien; se pierde esa foto, no el parte.
         */
        for (const foto of fotos) {
          await api.subirAdjunto('INCIDENCIA', incidencia.id, foto);
        }
        onCreada(incidencia);
      } catch (fallo: unknown) {
        setError(
          fallo instanceof ErrorApi ? t(idioma, fallo.mensajeClave) : t(idioma, 'error.servidor'),
        );
      } finally {
        setEnviando(false);
      }
    })();
  };

  return (
    <>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <form className="formulario" style={{ maxWidth: 'none' }} onSubmit={crear}>
        <div className="campo">
          <label htmlFor={ids.titulo}>{t(idioma, 'incidencia.tituloCampo')}</label>
          <input
            id={ids.titulo}
            required
            minLength={3}
            maxLength={300}
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
          />
        </div>

        {/*
          El nivel se elige entre cuatro botones y no en un desplegable: es la
          decision que cambia como se trata la incidencia, y en un desplegable
          se queda siempre el que venia puesto porque nadie lo abre.
        */}
        <div className="campo">
          <span className="etiqueta-campo">{t(idioma, 'incidencia.nivel')}</span>
          <div className="niveles" role="group" aria-label={t(idioma, 'incidencia.nivel')}>
            {NIVELES.map((n) => (
              <button
                key={n}
                type="button"
                className={`nivel-boton n${n}`}
                aria-pressed={nivel === n}
                onClick={() => setNivel(n)}
              >
                <b>N{n}</b>
                <small>{t(idioma, `incidencia.nivel.${n}`)}</small>
              </button>
            ))}
          </div>
        </div>

        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor={ids.area}>{t(idioma, 'incidencia.area')}</label>
            <select
              id={ids.area}
              value={area}
              onChange={(e) => setArea(e.target.value as AreaIncidencia)}
            >
              {AREAS.map((a) => (
                <option key={a} value={a}>
                  {t(idioma, `area.${a}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor={ids.coste}>{t(idioma, 'incidencia.costeEstimado')}</label>
            <input
              id={ids.coste}
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={coste}
              onChange={(e) => setCoste(e.target.value)}
            />
          </div>
        </div>

        <div className="campo">
          <label htmlFor={ids.descripcion}>{t(idioma, 'incidencia.descripcion')}</label>
          <textarea
            id={ids.descripcion}
            rows={4}
            maxLength={4000}
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </div>

        <div className="campo">
          <span className="etiqueta-campo">{t(idioma, 'incidencia.fotos')}</span>
          <div className="fila-flex">
            <label className="boton-fichero" htmlFor={`${ids.titulo}-fotos`}>
              {t(idioma, 'incidencia.anadirFotos')}
            </label>
            <input
              id={`${ids.titulo}-fotos`}
              type="file"
              accept="image/*"
              multiple
              className="solo-lectores"
              onChange={(e) => {
                const elegidas = [...(e.target.files ?? [])];
                // Se ACUMULAN: elegir de la galeria y luego hacer una foto son
                // dos gestos, y el segundo no puede borrar el primero.
                if (elegidas.length > 0) setFotos((p) => [...p, ...elegidas]);
                e.target.value = '';
              }}
            />
          </div>
          {fotos.length > 0 && (
            <div className="fila-flex">
              {fotos.map((f, i) => (
                <button
                  key={`${f.name}-${String(i)}`}
                  type="button"
                  className="secundario"
                  title={t(idioma, 'incidencia.quitarFoto')}
                  onClick={() => setFotos((p) => p.filter((_, j) => j !== i))}
                >
                  {f.name} ✕
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="acciones">
          <button type="submit" disabled={enviando || titulo.trim().length < 3}>
            {enviando ? t(idioma, 'app.cargando') : t(idioma, 'incidencia.crear')}
          </button>
          <button type="button" className="secundario" onClick={onCancelar}>
            {t(idioma, 'app.cancelar')}
          </button>
        </div>
      </form>
    </>
  );
}
